import { axiosInstance } from "../../../config/axios";
import endpoints from "../../../config/endpoints";
import { GenericResponse } from "../../../types";

export type Langs = 'en' | 'es' | 'fr' | 'de' | 'yo' ;

export interface UserPatient {
    id: number | string,
    username: string,
    email: string,
    first_name: string,
    last_name: string,
    role_id: number,
    total_calls: number,
    last_call_date: string,
    appointments_count: number,
    phone: string,
    dob?: string,
    address?: string,
    emergency_contact_name: string,
    emergency_contact_phone: string,
    emergency_contact_relationship: string,
    insurance_provider: string,
    insurance_policy_number: string,
    insurance_group_number: string,
    medical_history?: string[],
    medical_condition?: string,
    allergies?: string[],
    last_visit?: string,
    next_appointment?: string,
    language?: Langs,
    notes?: string,
    treatment_plans?:  Record<string, string>[],
    sms_logs?:  Record<string, string>[],
    email_logs?:  Record<string, string>[],
    dental_chart?:  Record<string, string>[],
    voice_to_charting_notes?:  Record<string, string>[],
    predictive_treatment_suggestions?:  Record<string, string>[],
    ai_suggested_medical_alerts?:  Record<string, string>[],
    ai_created?: boolean,
    created_by?: string,
    education_history?: Record<string, string>[],
    created_at: string,
    updated_at: string,
    preferred_language?: Langs,
 
}

interface CreateUserPayload {
    email: string,
    first_name?: string,
    last_name?: string,
    phone: string,
}
export interface UpdateUserPayload {
  first_name?: string,
  last_name?: string,
  email?: string,
  dob?: string,
  address?: string,
  emergency_contact_name?: string,
  emergency_contact_phone?: string,
  emergency_contact_relationship?: string,
  insurance_provider?: string,
  insurance_policy_number?: string,
  insurance_group_number?: string,
  preferred_language?: Langs,
  medical_condition?: string,
}

export interface Condition {
  condition: string;
  patient_count: number;
}

export const createUser = async (payload: CreateUserPayload): Promise<UserPatient> => {
  const response = await axiosInstance.post<UserPatient>(
    `${import.meta.env.PROD ? endpoints().users.createUser : `/api/v1${endpoints().users.createUser}`}`,
    payload
  );
  return response.data;
};


export const getUsers = async (
    params: {
        name: string | undefined,
        page: number, 
        limit: number
    }): Promise<GenericResponse<UserPatient[]>> => {
  const response = await axiosInstance.get<GenericResponse<UserPatient[]>>(
    `${import.meta.env.PROD ? endpoints().users.getUsers : `/api/v1${endpoints().users.getUsers}`}`,
    { params },
  );
  return response.data;
};

export const getUserById = async (id: number): Promise<UserPatient> => {
  const response = await axiosInstance.get<UserPatient>(
    `${import.meta.env.PROD ? endpoints(id).users.getUserById : `/api/v1${endpoints(id).users.getUserById}`}`,
  );
  return response.data;
};

export const updateUserById = async (id: number, payload: Partial<UpdateUserPayload>): Promise<UserPatient> => {
  const response = await axiosInstance.put<UserPatient>(
    `${import.meta.env.PROD ? endpoints(id).users.updateUserById : `/api/v1${endpoints(id).users.updateUserById}`}`,
    payload
  );
  return response.data;
};

export const getUserByPhone = async (phone: string): Promise<UserPatient> => {
  const response = await axiosInstance.get<UserPatient>(
    `${import.meta.env.PROD ? endpoints(phone).users.getUserByPhone : `/api/v1${endpoints(phone).users.getUserByPhone}`}`,
  );
  return response.data;
};

export const getUserAppointments = async (id: number): Promise<UserPatient[]> => {
  const response = await axiosInstance.get<UserPatient[]>(
    `${import.meta.env.PROD ? endpoints(id).users.getUserAppointments : `/api/v1${endpoints(id).users.getUserAppointments}`}`,
  );
  return response.data;
};

export const deleteUserById = async (id: number): Promise<void> => {
  await axiosInstance.delete(
    `${import.meta.env.PROD ? endpoints(id).users.deleteUserById : `/api/v1${endpoints(id).users.deleteUserById}`}`,
  );
};

export const getConditions = async (params:{
  limit: number,
}): Promise<Condition[]> => {
  const response = await axiosInstance.get<Condition[]>(
    `${import.meta.env.PROD ? endpoints().users.getConditions : `/api/v1${endpoints().users.getConditions}`}`,
    { params }
  );
  return response.data;
};

export interface UserEducationAssignment {
  id: number;
  user_id: number;
  content_id: number;
  content_title: string;
  content_description: string | null;
  content_type: string;
  content_url: string | null;
  video_channel: string | null;
  video_thumbnail_url: string | null;
  diagnosis: string;
  treatment_plan: string;
  status: 'sent' | 'pending' | 'failed';
  scheduled_send_date: string;
  scheduled_send_time: string;
  sent_at: string | null;
  return_date: string;
  failure_reason: string | null;
  assigned_by_admin_id: number;
  created_at: string;
  updated_at: string;
}

export interface UserEducationAssignmentPaginationData {
  total_items: number;
  page: number;
  limit: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface PaginatedUserEducationAssignments {
  data: UserEducationAssignment[];
  pagination: UserEducationAssignmentPaginationData;
}

export const getUserEducationAssignments = async (params: { 
  id: number, 
  limit: number,
  page: number,
  status?: "sent" | "pending" | "failed",
}): Promise<PaginatedUserEducationAssignments> => {
  const { id, ...queryParams } = params;
  const response = await axiosInstance.get<PaginatedUserEducationAssignments>(
    `${import.meta.env.PROD ? endpoints(id).users.getUserEducationAssignments : `/api/v1${endpoints(id).users.getUserEducationAssignments}`}`,
    { params: queryParams }
  );
  return response.data;
};


